import { fieldValueError, labToSrgb } from '@dyeatlas/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { isRecordEditable, type useRecordDraft } from '../useRecordDraft'
import { FieldControl } from './FieldControl'
import { MORDANT_NOTES, type FieldDef } from './fields'
import { Swatch } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Field } from '@/components/ui/Field'
import { Card, Notice } from '@/components/ui/misc'
import { useMasterData } from '@/hooks/useMasterData'
import { api, ApiError } from '@/lib/api'

type Draft = ReturnType<typeof useRecordDraft>

/**
 * A reference is a catalogue entry for a historical recipe, so it is a single page
 * rather than a wizard: there is no process to walk through, only provenance,
 * materials and the colour as recorded.
 */
const SECTIONS: { title: string; description: string; fields: FieldDef[] }[] = [
  {
    title: 'Source',
    description: 'Where this recipe comes from.',
    fields: [
      { name: 'title', label: 'Reference title', kind: 'text', required: true, wide: true },
      { name: 'manuscriptId', label: 'Manuscript', kind: 'select', vocabulary: 'manuscripts' },
      { name: 'recipeNumber', label: 'Recipe number', kind: 'text', placeholder: 'No. 42' },
      { name: 'referenceYear', label: 'Year', kind: 'number', placeholder: '1778' },
      { name: 'researcherName', label: 'Transcribed by', kind: 'text', required: true },
      { name: 'institutionId', label: 'Institution', kind: 'select', vocabulary: 'institutions' },
    ],
  },
  {
    title: 'Materials',
    description: 'What the recipe calls for.',
    fields: [
      { name: 'texFiberId', label: 'Fiber', kind: 'select', vocabulary: 'fibers', required: true },
      {
        name: 'dyeSourceIds',
        label: 'Dye sources',
        kind: 'multiselect',
        vocabulary: 'dyeSources',
        required: true,
        wide: true,
        hint: 'Select every dye the recipe calls for, main dye first.',
      },
      {
        name: 'morMordantIds',
        label: 'Mordants',
        kind: 'multiselect',
        vocabulary: 'mordants',
        required: true,
        wide: true,
      },
      MORDANT_NOTES,
    ],
  },
  {
    title: 'Colour as recorded',
    description: 'Optional: the measured colour, where the source or a surviving sample gives one.',
    fields: [
      { name: 'resLabL', label: 'CIELAB L*', kind: 'number' },
      { name: 'resLabA', label: 'CIELAB a*', kind: 'number' },
      { name: 'resLabB', label: 'CIELAB b*', kind: 'number' },
      { name: 'notes', label: 'Notes', kind: 'textarea', wide: true },
    ],
  },
]

export function ReferenceForm({ draft }: { draft: Draft }) {
  const navigate = useNavigate()
  const { options } = useMasterData()
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const { values, setField, record } = draft

  const readOnly = !isRecordEditable(record)

  // Only finished experiments are worth cross-linking to a reference.
  const { data: experiments } = useQuery({
    queryKey: ['records', 'linkable'],
    queryFn: () =>
      api.get<{ records: { id: string; code: string; title: string }[] }>(
        '/records?kind=experiment&status=finished,in_review,published&includeProjects=1',
      ),
  })

  const finish = useMutation({
    mutationFn: async () => {
      await draft.flush()
      return api.post<{ record: { code: string } }>(`/records/${record!.id}/finish`)
    },
    onSuccess: (result) => {
      toast.success(`Finished as ${result.record.code}.`)
      navigate('/workspace/finished')
    },
    onError: (failure) => {
      if (failure instanceof ApiError) {
        setFieldErrors(failure.fieldErrors)
        toast.error(failure.message)
      }
    },
  })

  const lab = [values.resLabL, values.resLabA, values.resLabB].map(Number)
  const swatch = lab.every(Number.isFinite)
    ? labToSrgb({ L: lab[0], a: lab[1], b: lab[2] })
    : null

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Notice tone="neutral">
        References describe a recipe as written in a historical source. Link the experiments that
        reproduce it so the two can be compared side by side.
      </Notice>

      {SECTIONS.map((section) => (
        <Card key={section.title} className="p-6">
          <header className="mb-4">
            <h2 className="font-display text-lg font-semibold text-ink">{section.title}</h2>
            <p className="mt-0.5 text-[13px] text-ink-muted">{section.description}</p>
          </header>

          <fieldset disabled={readOnly} className="grid grid-cols-2 gap-x-5 gap-y-4">
            {section.fields.map((field) => (
              <FieldControl
                key={field.name}
                field={field}
                value={values[field.name]}
                error={fieldErrors[field.name] ?? fieldValueError('reference', field.name, values[field.name])}
                options={options}
                onChange={(value) => {
                  if (fieldValueError('reference', field.name, value)) {
                    draft.setValues((current) => ({ ...current, [field.name]: value }))
                  } else {
                    setField(field.name, value)
                  }
                }}
              />
            ))}
          </fieldset>

          {section.title === 'Colour as recorded' && swatch && (
            <div className="mt-4 flex items-center gap-3 rounded-[var(--radius-control)] border border-line bg-paper-muted p-3">
              <Swatch hex={swatch.hex} size="lg" className="rounded-[var(--radius-control)]" />
              <div>
                <p className="text-[15px] font-semibold text-ink">
                  L* {lab[0]} · a* {lab[1]} · b* {lab[2]}
                </p>
                <p className="text-[12px] text-ink-muted">
                  <span className="font-mono">{swatch.hex}</span>
                  {swatch.outOfGamut && (
                    <span className="text-caution"> (outside sRGB: nearest displayable colour)</span>
                  )}
                </p>
              </div>
            </div>
          )}
        </Card>
      ))}

      <Card className="p-6">
        <header className="mb-4">
          <h2 className="font-display text-lg font-semibold text-ink">Connected experiments</h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            Experiments that reproduce this recipe. These appear on both records.
          </p>
        </header>
        <Field label="Experiments">
          <MultiSelect
            value={(values.connectedExperimentIds as string[]) ?? []}
            onChange={(next) => setField('connectedExperimentIds', next)}
            placeholder="No experiments linked"
            options={(experiments?.records ?? []).map((row) => ({
              value: row.id,
              label: row.title || 'Untitled',
              description: row.code,
            }))}
          />
        </Field>
      </Card>

      <footer className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={() => void draft.flush().then(() => navigate('/workspace/drafts'))}>
          Save and close
        </Button>
        <Button loading={finish.isPending} disabled={readOnly} onClick={() => finish.mutate()}>
          <Check className="size-4" />
          Finish reference
        </Button>
      </footer>
    </div>
  )
}
